import { gql } from '@apollo/client';
import { useQuery, UseQueryResult } from 'react-query';
import { graphqlClient } from 'bk-web-app';
import { GlobalService } from '../types/GlobalService';

const GLOBAL_SERVICE_QUERY = gql`
  query GlobalService($id: ID!) {
    globalService(id: $id) {
      id
      dataSource {
        id
        name
      }
      action {
        id
        name
      }
      status
    }
  }
`;

interface GraphQLGlobalService {
  id: string;
  dataSource: { id: string; name: string };
  action: { id: string; name: string };
  status: string;
}

const mapGraphQLGlobalService = (gs: GraphQLGlobalService): GlobalService => ({
  globalServiceId: gs.id,
  dataSourceId: gs.dataSource.id,
  dataSourceName: gs.dataSource.name,
  actionId: gs.action.id,
  actionName: gs.action.name,
  status: gs.status,
});

const get = async (apiUrl: string, globalServiceId: string): Promise<GlobalService> => {
  if (!apiUrl || !globalServiceId) {
    throw new Error('Required properties are not set');
  }

  const response: Response = await fetch(`${apiUrl}/global-service/${globalServiceId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch global service');
  }

  return (await response.json()) as GlobalService;
};

const getViaGraphQL = async (globalServiceId: string): Promise<GlobalService> => {
  const { data } = await graphqlClient.query({
    query: GLOBAL_SERVICE_QUERY,
    variables: { id: globalServiceId },
    context: { useV2: true },
  });
  return mapGraphQLGlobalService(data.globalService);
};

interface UseFetchGlobalServiceProps {
  globalServiceId?: string;
  useGraphQL?: boolean;
}

export const useFetchGlobalService = ({
  globalServiceId,
  useGraphQL = false,
}: UseFetchGlobalServiceProps): UseQueryResult<GlobalService> => {
  const apiUrl = window.BENCH?.auApi?.FIND_GLOBAL_SERVICES_API_URL || '';

  return useQuery({
    queryKey: ['getGlobalService', globalServiceId, useGraphQL],
    enabled: !!globalServiceId,
    queryFn: () => {
      if (!globalServiceId) {
        throw new Error('globalServiceId is required');
      }
      return useGraphQL ? getViaGraphQL(globalServiceId) : get(apiUrl, globalServiceId);
    },
  });
};
